import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { isNative } from './native';

/**
 * Push notifications from friends (docs/FRIENDS.md, step 4), through @capacitor/push-notifications.
 * iPhone only for now (Android needs Firebase, which waits for Google Play). Every call is safe
 * anywhere: elsewhere it does nothing.
 */

/** Whether this device can get friend notifications (dev builds: `?pushPreview` draws the screens). */
export function pushSupported(): boolean {
  if (import.meta.env.DEV && new URLSearchParams(location.search).has('pushPreview')) return true;
  return isNative() && Capacitor.getPlatform() === 'ios';
}

/**
 * Whether the app may show them. With `ask`, a player who has not answered yet gets the system
 * prompt (only ever after they said yes in the game, never at launch).
 */
export async function pushAllowed(ask: boolean): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    let p = await PushNotifications.checkPermissions();
    if (ask && p.receive !== 'granted' && p.receive !== 'denied') {
      p = await PushNotifications.requestPermissions();
    }
    return p.receive === 'granted';
  } catch {
    return false;
  }
}

/** Whether notifications are switched off for the app in the phone's Settings. */
export async function pushDenied(): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    return (await PushNotifications.checkPermissions()).receive === 'denied';
  } catch {
    return false;
  }
}

/** This phone's token from Apple, or why there is none. */
export type PushTokenAnswer = { token: string } | { error: string };

/** Registers with Apple: this phone's token, or Apple's reason (no answer in 15 seconds counts). */
export async function pushToken(): Promise<PushTokenAnswer> {
  if (!pushSupported()) return { error: 'not supported' };
  let done = false;
  let finish: (a: PushTokenAnswer) => void = () => {};
  const answer = new Promise<PushTokenAnswer>((resolve) => {
    finish = (a) => {
      if (done) return;
      done = true;
      resolve(a);
    };
  });
  try {
    // Both listeners are in place before asking, so a quick answer is not missed.
    const handles = await Promise.all([
      PushNotifications.addListener('registration', (t) => finish({ token: t.value })),
      PushNotifications.addListener('registrationError', (e) => finish({ error: e.error })),
    ]);
    void answer.then(() => handles.forEach((h) => void h.remove()));
    await PushNotifications.register();
  } catch (e) {
    finish({ error: e instanceof Error ? e.message : 'register failed' });
  }
  setTimeout(() => finish({ error: 'no answer from Apple' }), 15_000);
  return answer;
}

/** Notifications already acted on (one that launched the app can be handed over twice). */
const tapped = new Set<string>();

/** Calls `fn` with a notification's data when the player taps one (also one that opened the app). */
export function onPushTapped(fn: (data: Record<string, unknown>) => void): void {
  if (!pushSupported()) return;
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    if (action.actionId === 'dismiss') return;
    const id = action.notification.id;
    if (id) {
      if (tapped.has(id)) return;
      tapped.add(id);
    }
    fn((action.notification.data ?? {}) as Record<string, unknown>);
  }).catch(() => {
    // Not available here (a dev preview in a browser).
  });
}
